import {
  Layers, Users, ShoppingCart, FileText, Package,
  Truck, Receipt, BarChart3, BookOpen, Shield, PieChart,
  AlertTriangle, Scale, GitBranch, LayoutDashboard,
  Archive, FilePlus, Sparkles, UserCheck, ListChecks,
  Activity, FilePen, CalendarClock, Calculator, FileSearch,
  CloudLightning, Gavel, ClipboardCheck, HeartHandshake,
  ShieldAlert, Database, HandCoins, Globe
} from 'lucide-react';

export const PROCUREMENT_MODULES = [
  {
    key: 'scm',
    label: 'Category & Spend',
    subtitle: 'Strategic Category Planning',
    icon: Layers,
    color: '#10B981',
    colorEnd: '#059669',
    children: [
      { path: '/app/scm/category-performance', label: 'Category Performance' },
      { path: '/app/scm/taxonomy', label: 'Taxonomy & Hierarchy' },
      { path: '/app/scm/spend-cube', label: 'Spend Analytics' },
      { path: '/app/scm/kraljic', label: 'Kraljic Matrix' },
      { path: '/app/scm/strategy-doc', label: 'Strategy Documents' },
      { path: '/app/scm/market-intel', label: 'Market Intelligence' },
      { path: '/app/scm/demand', label: 'Demand Forecasting' },
      { path: '/app/scm/pr-consolidation', label: 'PR Consolidation Agent' },
    ],
  },
  {
    key: 'srm',
    label: 'Supplier Management',
    subtitle: 'Supplier Lifecycle',
    icon: Users,
    color: '#14B8A6',
    colorEnd: '#0D9488',
    children: [
      { path: '/app/srm/registration', label: 'Registration & Onboarding' },
      { path: '/app/srm/qualification', label: 'Qualification & Category Approval' },
      { path: '/app/srm/performance', label: 'Performance Management' },
      { path: '/app/srm/relationship', label: 'Relationship Management' },
      { path: '/app/srm/risk', label: 'Risk Management' },
      { path: '/app/srm/data-governance', label: 'Information & Data Management' },
      { path: '/app/srm/diversity', label: 'Diversity & Inclusion' },
      { path: '/app/srm/collaboration', label: 'Collaboration & Portal' },
      { path: '/app/srm/extras', label: 'L4 Closure (E-Sign · Risk · AVL · Certs · GL Recon · CAPA)' },
    ],
  },
  {
    key: 'sourcing',
    label: 'Strategic Sourcing',
    subtitle: 'Event Planning → Award → Notification',
    icon: ShoppingCart,
    color: '#6366F1',
    colorEnd: '#4F46E5',
    children: [
      { path: '/app/sourcing/events', label: 'Sourcing Strategy & Event Planning' },
      { path: '/app/sourcing/supplier-invitation', label: 'Supplier Identification & Invitation' },
      { path: '/app/sourcing/clarifications', label: 'Clarification & Pre-Bid Management' },
      { path: '/app/sourcing/bid-submission', label: 'Bid / Proposal Submission' },
      { path: '/app/sourcing/bid-opening', label: 'Bid Opening & Comparison' },
      { path: '/app/sourcing/evaluation', label: 'Technical & Commercial Evaluation' },
      { path: '/app/sourcing/auction', label: 'eAuction Execution' },
      { path: '/app/sourcing/bafo', label: 'Best and Final Offer (BAFO)' },
      { path: '/app/sourcing/framework-agreements', label: 'Framework Agreement Management' },
      { path: '/app/sourcing/direct-award', label: 'Direct Award Management' },
      { path: '/app/sourcing/award-recommendation', label: 'Award Recommendation, Approval & Closure' },
      { path: '/app/sourcing/communication-engine', label: 'Notification & Communication Engine' },
      { path: '/app/sourcing/analytics', label: 'Sourcing Analytics & Reporting' },
      { path: '/app/sourcing/portal-admin', label: 'Supplier Portal — Sourcing Interfaces' },
      { path: '/app/sourcing/itt-compliance', label: 'ITT Regulatory Compliance' },
      { path: '/app/sourcing/extras', label: 'L4 Closure (Live Auction · BAFO · Framework→PR · Award→Contract · SLA · FX Grid)' },
    ],
  },
  {
    // iter-225 — Requisitions: 12 sidebar entries → 4. All entries below
    // pointed to `RequisitionsPage` with `?tab=…`; the page already exposes
    // its own tab bar for Create · Dashboard · Conversational · NL Intake ·
    // Policy · Matrix · Conditions · Approvals · L4 Closure, so having
    // them all in the sidebar was pure duplication. Deep-link routes stay
    // intact — only the sidebar was trimmed.
    key: 'pr',
    label: 'Requisitions',
    subtitle: 'Purchase Requisitions',
    icon: FileText,
    color: '#EC4899',
    colorEnd: '#DB2777',
    children: [
      { path: '/app/requisitions?tab=create',    label: 'Create PR (AI-enriched)' },
      { path: '/app/requisitions?tab=dashboard', label: 'PR Workbench & Dashboard' },
      { path: '/app/requisitions?tab=approvals', label: 'Approvals & Matrix' },
      { path: '/app/bridges/extras?tab=budget',  label: 'Bridge — Budget · MRP · Punchout' },
    ],
  },
  {
    // iter-225 — PO: 10 sidebar entries → 4. Everything below pointed to
    // `PurchaseOrdersPage` which owns Workbench · Create · Detail ·
    // Analytics · Co-Pilot · L4 as internal tabs. Deep-link routes retained.
    key: 'po',
    label: 'Purchase Orders',
    subtitle: 'PO Management',
    icon: Package,
    color: '#22C55E',
    colorEnd: '#16A34A',
    children: [
      { path: '/app/po?tab=workbench', label: 'PO Workbench' },
      { path: '/app/po?tab=create',    label: 'Create PO (Manual · PR · Framework · DA)' },
      { path: '/app/po?tab=copilot',   label: 'PO Co-Pilot (AI)' },
      { path: '/app/po?tab=analytics', label: 'PO Analytics & L4 Closure' },
    ],
  },
  {
    // iter-225 — GRN: 5 sidebar entries → 3. GoodsReceiptPage owns
    // Dashboard · ASN · Receipt · Inspection · Returns · SES · Analytics as
    // internal tabs; sidebar now shows only the high-level entry points.
    key: 'grn',
    label: 'Goods Receipt',
    subtitle: 'Quality & Inspection',
    icon: Truck,
    color: '#F97316',
    colorEnd: '#EA580C',
    children: [
      { path: '/app/grn/receipt',     label: 'Receipt & ASN Workbench' },
      { path: '/app/grn/inspection',  label: 'Quality Inspection & Returns' },
      { path: '/app/grn?tab=l4-closure', label: 'L4 Closure (Blob · WMS · FX · ASN · Anomaly)' },
    ],
  },
  {
    // iter-225 — Invoices: 9 sidebar entries → 4. All the ?tab= entries
    // pointed to `InvoiceWorkbenchPage` which owns Dashboard · Intake ·
    // Keyer · Match · Approvals · PayRuns · Analytics · Fraud tabs.
    key: 'invoice',
    label: 'Invoices & Payment',
    subtitle: 'Invoice Processing',
    icon: Receipt,
    color: '#F59E0B',
    colorEnd: '#D97706',
    children: [
      { path: '/app/invoices?tab=dashboard', label: 'Dashboard & KPIs' },
      { path: '/app/invoices?tab=intake',    label: 'Ingestion · OCR · 3-Way Match' },
      { path: '/app/invoices?tab=payruns',   label: 'Approvals & Payment Runs' },
      { path: '/app/invoices?tab=analytics', label: 'Analytics · Fraud · Admin' },
    ],
  },
  {
    // iter-225 — Catalogue: 3 sidebar entries → 1. `CataloguePage` owns
    // Internal · Punchout · Guided as internal tabs.
    key: 'catalogue',
    label: 'Catalogue',
    subtitle: 'Guided Buying',
    icon: BookOpen,
    color: '#A855F7',
    colorEnd: '#9333EA',
    children: [
      { path: '/app/catalogue', label: 'Catalogue Workbench (Internal · Punchout · Guided)' },
    ],
  },
];

export const PRE_AWARD_MODULES = PROCUREMENT_MODULES;

// iter-225 — Contracts sidebar consolidated. Multiple sidebar entries used
// to point at the same page component (e.g. `/cm/portfolio`, `/cm/bonds`,
// `/cm/insurance` all rendered CMPortfolioPage which already exposes a
// Renewals · Bonds · Insurance tab bar — duplicate tabs visible in the
// screenshot). We keep the deep-link routes in App.js so external links
// keep working; only the sidebar was trimmed to one entry per unique
// page + its own internal tabs. Groups are also re-sequenced to follow
// the real contract lifecycle: Types → Request → Authoring → Review →
// Sign → Obligations → Performance → Amendments → Renewal.
export const CONTRACTS_MODULES = [
  {
    key: 'cm-types',
    label: 'Contract Types & Repository',
    subtitle: 'Contract Classification',
    icon: Archive,
    color: '#64748B',
    colorEnd: '#475569',
    children: [
      { path: '/app/cm/contract-types', label: 'Supported Contract Types' },
      { path: '/app/cm/repository', label: 'Contract Repository Architecture' },
      { path: '/app/cm/portfolio', label: 'Portfolio Dashboard (Renewals · Bonds · Insurance)' },
      { path: '/app/cm/integrations', label: 'Integration Hub (Supplier 360 · PR/PO · Spend · Sourcing)' },
    ],
  },
  {
    key: 'cm-request',
    label: 'Contract Request & Initiation',
    subtitle: 'Intake & Risk Assessment',
    icon: FilePlus,
    color: '#3B82F6',
    colorEnd: '#2563EB',
    children: [
      { path: '/app/cm/request-form', label: 'Contract Request Form' },
      { path: '/app/cm/risk-assessment', label: 'Pre-Contract Risk Assessment' },
    ],
  },
  {
    key: 'cm-authoring',
    label: 'AI-Assisted Authoring',
    subtitle: 'Smart Drafting',
    icon: Sparkles,
    color: '#8B5CF6',
    colorEnd: '#7C3AED',
    children: [
      { path: '/app/cm/clause-library', label: 'Clause Library & Templates' },
      { path: '/app/cm/templates', label: 'Contract Templates' },
      { path: '/app/cm/ai-drafting', label: 'AI Authoring Studio (Draft · Generate · Collaborate · Self-Review)' },
    ],
  },
  {
    key: 'cm-review',
    label: 'Review & Negotiation',
    subtitle: 'Reviews · Redlines · Signing',
    icon: UserCheck,
    color: '#F59E0B',
    colorEnd: '#D97706',
    children: [
      { path: '/app/cm/internal-review', label: 'Internal · Multi-Stakeholder · Legal Review' },
      { path: '/app/cm/negotiation', label: 'Negotiation · Redlines · Playbooks' },
      { path: '/app/cm/approval-signing', label: 'Approval · E-Signature Envelopes' },
    ],
  },
  {
    key: 'cm-obligation',
    label: 'Obligation Management',
    subtitle: 'Extraction · Tracking · FM',
    icon: ListChecks,
    color: '#06B6D4',
    colorEnd: '#0891B2',
    children: [
      { path: '/app/cm/obligation-tracking', label: 'Obligation Calendar · Extraction · FM · Dashboard' },
    ],
  },
  {
    key: 'cm-performance',
    label: 'Performance Monitoring',
    subtitle: 'KPIs · SLAs · Health',
    icon: Activity,
    color: '#10B981',
    colorEnd: '#059669',
    children: [
      { path: '/app/cm/performance', label: 'Health · KPIs · SLA · Financial Monitoring' },
    ],
  },
  {
    key: 'cm-amendment',
    label: 'Amendments & Change Orders',
    subtitle: 'Amendments · Change Orders · Variations',
    icon: FilePen,
    color: '#F97316',
    colorEnd: '#EA580C',
    children: [
      { path: '/app/cm/amendments', label: 'Amendments · Change Orders · Variations · Cost Impact' },
    ],
  },
  {
    key: 'cm-renewal',
    label: 'Renewal & Expiry',
    subtitle: 'Lifecycle Management',
    icon: CalendarClock,
    color: '#F43F5E',
    colorEnd: '#E11D48',
    children: [
      { path: '/app/cm/renewal', label: 'Renewal AI · Clone · Sourcing Event' },
      { path: '/app/cm/expiry-alerts', label: 'Expiry Alerts · Closure' },
    ],
  },
];

export const CLAIMS_LD_MODULES = [
  {
    key: 'cld-initiation',
    label: 'Claim Initiation & Registration',
    subtitle: 'Dashboard · Register · FM · Evidence',
    icon: AlertTriangle,
    color: '#F59E0B',
    colorEnd: '#D97706',
    children: [
      { path: '/app/cld/v2', label: 'CLD Workbench (Dashboard)' },
      { path: '/app/cld/register', label: 'Claim Register' },
      { path: '/app/cld/manual-initiation', label: 'New Claim Wizard' },
      { path: '/app/cld/fm-notice-v2', label: 'Force Majeure Notices' },
      { path: '/app/cld/evidence-v2', label: 'Evidence Repository' },
    ],
  },
  {
    key: 'cld-ld-engine',
    label: 'LD Computation Engine',
    subtitle: 'Liquidated Damages · Notices · Recovery',
    icon: Calculator,
    color: '#EF4444',
    colorEnd: '#DC2626',
    children: [
      { path: '/app/cld/ld-clause-library', label: 'LD Clause Library' },
      { path: '/app/cld/ld-config', label: 'LD Configuration per Contract' },
      { path: '/app/cld/ld-notice', label: 'LD Notice Generation & Dispatch' },
      { path: '/app/cld/ld-recovery', label: 'LD Recovery Dashboard' },
    ],
  },
  {
    key: 'cld-quantum',
    label: 'Quantum & Negotiation',
    subtitle: 'Hudson/Emden/Eichleay · Monte Carlo · Settlement',
    icon: FileSearch,
    color: '#06B6D4',
    colorEnd: '#0891B2',
    children: [
      { path: '/app/cld/quantum-workbench', label: 'Quantum Workbench' },
      { path: '/app/cld/quantum-engine', label: 'Monte Carlo Engine' },
      { path: '/app/cld/negotiation-table', label: 'Negotiation Table' },
    ],
  },
  {
    key: 'cld-enforcement',
    label: 'Enforcement & Recovery',
    subtitle: 'Adjudication · Recovery · Analytics',
    icon: Gavel,
    color: '#8B5CF6',
    colorEnd: '#7C3AED',
    children: [
      { path: '/app/cld/enforcement-board', label: 'Enforcement Board' },
      { path: '/app/cld/recovery-ledger', label: 'Recovery Ledger' },
      { path: '/app/cld/adjudication', label: 'Adjudication Workbench' },
      { path: '/app/cld/adjudication-list', label: 'Adjudication Cases (master list)' },
      { path: '/app/cld/portfolio-analytics', label: 'Portfolio Analytics' },
      { path: '/app/cld/predictive-risk', label: 'Predictive Risk Panel' },
    ],
  },
];

export const STANDALONE_ITEMS = [];

export const DASHBOARD_ITEM = {
  key: 'dashboard',
  label: 'Dashboard',
  subtitle: 'Overview & Analytics',
  path: '/app',
  icon: LayoutDashboard,
  color: '#4F46E5',
  colorEnd: '#3B82F6',
  isLink: true,
};

export function findNavItem(pathname) {
  const allModules = [...PROCUREMENT_MODULES, ...CONTRACTS_MODULES, ...CLAIMS_LD_MODULES];
  for (const mod of allModules) {
    const child = mod.children.find(c => pathname === c.path || pathname.startsWith(c.path + '/'));
    if (child) return { module: mod.label, subModule: child.label, moduleKey: mod.key };
  }
  const item = STANDALONE_ITEMS.find(i => pathname === i.path || pathname.startsWith(i.path + '/'));
  if (item) return { module: item.label, subModule: null, moduleKey: 'standalone' };
  return null;
}

export function getCurrentModule(pathname) {
  // Children paths may contain query strings (e.g. /app/requisitions?tab=dashboard).
  // Match only the path-portion so /app/requisitions resolves to the Requisitions module.
  const stripQ = (p) => (p || '').split('?')[0];
  const matches = (mod) => mod.children?.some(c => {
    const cp = stripQ(c.path);
    return pathname === cp || pathname.startsWith(cp + '/');
  });
  for (const mod of PROCUREMENT_MODULES) { if (matches(mod)) return mod; }
  for (const mod of CONTRACTS_MODULES)   { if (matches(mod)) return mod; }
  for (const mod of CLAIMS_LD_MODULES)   { if (matches(mod)) return mod; }
  return null;
}
